import type { ComponentChildren } from 'preact';
import { api } from '../api';
import type { T } from '../i18n';
import { set } from '../state';
import type { AppState, Tab } from '../state';
import { MailboxIcon, ProfileIcon, StampsIcon, TrackIcon, WriteIcon } from './Icons';

const TABS: Array<{ key: Tab; label: Parameters<T>[0]; icon: () => ComponentChildren }> = [
  { key: 'mailbox', label: 'navMailbox', icon: MailboxIcon },
  { key: 'track', label: 'navTrack', icon: TrackIcon },
  { key: 'compose', label: 'navWrite', icon: WriteIcon },
  { key: 'album', label: 'navStamps', icon: StampsIcon },
  { key: 'profile', label: 'navProfile', icon: ProfileIcon },
];

/** The design's sidebar; on a portrait phone it becomes the bottom tab bar. */
export function Sidebar({ s, t }: { s: AppState; t: T }) {
  return (
    <aside class="side">
      <div class="side-brand">
        <img class="side-logo" src="/assets/logo2.png" alt="Qué fue, pana?" />
        <LangToggle s={s} />
      </div>
      <nav class="nav" aria-label="Qué fue, pana?">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            class="nav-item"
            aria-current={s.tab === tab.key ? 'page' : undefined}
            onClick={() =>
              set({
                tab: tab.key,
                openCardId: null,
                sentCard: null,
                zoomIndex: null,
                pickerOpen: false,
                addressOpen: false,
                confirmCancelId: null,
                editOpen: false,
              })
            }
          >
            <span class="nav-label">{t(tab.label)}</span>
            {tab.icon()}
          </button>
        ))}
      </nav>
    </aside>
  );
}

/** Portrait phones only: the brand and language toggle, which the sidebar holds in landscape. */
export function Topbar({ s }: { s: AppState }) {
  return (
    <header class="topbar">
      {/* The logo spells out the name, so it stands alone here. */}
      <img class="topbar-logo" src="/assets/logo2.png" alt="Qué fue, pana?" />
      <LangToggle s={s} />
    </header>
  );
}

export function LangToggle({ s }: { s: AppState }) {
  const next = s.lang === 'en' ? 'es' : 'en';
  return (
    <button
      // Not a submit button: the toggle also sits inside the first-sign-in form.
      type="button"
      class="lang"
      data-lang={s.lang}
      aria-label={s.lang === 'en' ? 'Cambiar a español' : 'Switch to English'}
      onClick={() => {
        set({ lang: next, user: s.user ? { ...s.user, lang: next } : null });
        if (s.user) {
          const { handle, country } = s.user;
          void api.updateMe({ handle, country, lang: next }).catch(() => undefined);
        }
      }}
    >
      <i class="lang-knob" />
      <span>es</span>
      <span>en</span>
    </button>
  );
}

export function Toast({ s }: { s: AppState }) {
  if (!s.toast) return null;
  return (
    <div class="toast" data-kind={s.toast.kind} role="status" aria-live="polite">
      {s.toast.text}
    </div>
  );
}

export function Loading({ t }: { t: T }) {
  return (
    <div class="center-fill">
      <div class="spinner" />
      <div class="kicker">{t('loading')}</div>
    </div>
  );
}
